import React, { useState, useEffect } from 'react';
import { 
  Search, Loader2, DollarSign, ChevronLeft, ChevronRight,
  Eye, Ban, ArrowDownLeft, ArrowUpRight, X, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { paymentsAPI, vendorsAPI, crmAPI, checksAPI, bankAccountsAPI } from '../../utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'ach', label: 'ACH' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const formatCents = (cents) => {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const AdminPayments = ({ onViewPayment }) => {
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(''); // Show all by default
  const [filterVendor, setFilterVendor] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Detail Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Check Creation Modal
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [checkPreview, setCheckPreview] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [checkDate, setCheckDate] = useState(new Date().toISOString().split('T')[0]);
  const [checkMemo, setCheckMemo] = useState('');
  const [creatingCheck, setCreatingCheck] = useState(false);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filterType) params.payment_type = filterType;
      if (filterVendor) params.vendor_id = filterVendor;
      if (filterCustomer) params.customer_id = filterCustomer;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await paymentsAPI.getPayments(params);
      setPayments(res.payments || []);
      setTotalPages(res.total_pages || 1);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error('Failed to load payments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await vendorsAPI.getVendors({ is_active: true, page_size: 200 });
      setVendors(res.vendors || []);
    } catch (err) {
      console.error('Failed to fetch vendors', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await crmAPI.getAccounts({ is_active: true, page_size: 200 });
      setCustomers(res.accounts || []);
    } catch (err) {
      console.error('Failed to fetch customers', err);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const accounts = await bankAccountsAPI.getAccounts({ is_active: true });
      setBankAccounts(accounts || []);
      // Set default account
      const defaultAcc = accounts?.find(a => a.is_default);
      if (defaultAcc) setSelectedBankAccount(defaultAcc.id);
    } catch (err) {
      console.error('Failed to fetch bank accounts', err);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCustomers();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterType, filterVendor, filterCustomer, filterStatus, dateFrom, dateTo]);

  // Open Write Check modal for a vendor payment
  const openWriteCheckModal = async (paymentId) => {
    try {
      const preview = await checksAPI.previewFromPayment(paymentId);
      setCheckPreview(preview);
      setCheckMemo(preview.memo || '');
      setCheckDate(new Date().toISOString().split('T')[0]);
      if (preview.default_bank_account_id) {
        setSelectedBankAccount(preview.default_bank_account_id);
      }
      setShowCheckModal(true);
    } catch (err) {
      toast.error('Failed to load payment details for check');
      console.error(err);
    }
  };

  // Create check from payment
  const handleCreateCheckFromPayment = async (printNow = false) => {
    if (!selectedBankAccount) {
      toast.error('Please select a bank account');
      return;
    }
    
    setCreatingCheck(true);
    try {
      const newCheck = await checksAPI.createCheck({
        bank_account_id: selectedBankAccount,
        date: checkDate,
        payee_name: checkPreview.payee_name,
        payee_address: checkPreview.payee_address || null,
        amount_cents: checkPreview.amount_cents,
        memo: checkMemo || null,
        payment_id: checkPreview.payment_id,
        vendor_id: checkPreview.vendor_id,
        print_now: printNow
      });
      
      toast.success(`Check #${newCheck.check_number} created`);
      setShowCheckModal(false);
      setCheckPreview(null);
      
      if (printNow) {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/checks/${newCheck.id}/pdf`, '_blank');
      }
      
      fetchPayments(); // Refresh to update UI
    } catch (err) {
      toast.error(err.message || 'Failed to create check');
      console.error(err);
    } finally {
      setCreatingCheck(false);
    }
  };

  const openDetailModal = async (paymentId) => {
    // If parent provided onViewPayment, use that for full-page navigation
    if (onViewPayment) {
      onViewPayment(paymentId);
      return;
    }
    
    // Fallback to modal for standalone usage
    setLoadingDetail(true);
    setShowDetailModal(true);
    try {
      const res = await paymentsAPI.getPayment(paymentId);
      setPaymentDetails(res);
      setSelectedPayment(res.payment);
    } catch (err) {
      toast.error('Failed to load payment details');
      setShowDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleVoidPayment = async (paymentId) => {
    if (!window.confirm('Void this payment? This will reverse all bill/invoice applications.')) return;
    try {
      await paymentsAPI.voidPayment(paymentId);
      toast.success('Payment voided');
      if (showDetailModal && selectedPayment?.id === paymentId) {
        openDetailModal(paymentId);
      }
      fetchPayments();
    } catch (err) {
      toast.error(err.message || 'Failed to void payment');
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Payments</h2>
          <p className="text-gray-500 text-sm mt-1">View and manage all payments (vendor expenses & customer receipts)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-36">
            <label className="block text-sm text-gray-600 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => { 
                setFilterType(e.target.value); 
                setFilterVendor('');
                setFilterCustomer('');
                setPage(1); 
              }}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="payment-type-filter"
            >
              <option value="">All Types</option>
              <option value="expense">Expense (Outbound)</option>
              <option value="sales">Sales (Inbound)</option>
            </select>
          </div>
          
          {/* Vendor filter - show when type is empty or expense */}
          {(filterType === '' || filterType === 'expense') && (
            <div className="w-48">
              <label className="block text-sm text-gray-600 mb-1">Vendor</label>
              <select
                value={filterVendor}
                onChange={(e) => { setFilterVendor(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="payment-vendor-filter"
              >
                <option value="">All Vendors</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Customer filter - show when type is empty or sales */}
          {(filterType === '' || filterType === 'sales') && (
            <div className="w-48">
              <label className="block text-sm text-gray-600 mb-1">Customer</label>
              <select
                value={filterCustomer}
                onChange={(e) => { setFilterCustomer(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border rounded-lg"
                data-testid="payment-customer-filter"
              >
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          
          <div className="w-32">
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="payment-status-filter"
            >
              <option value="">All</option>
              <option value="posted">Posted</option>
              <option value="void">Void</option>
            </select>
          </div>
          <div className="w-36">
            <label className="block text-sm text-gray-600 mb-1">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              data-testid="payment-date-from"
            />
          </div>
          <div className="w-36">
            <label className="block text-sm text-gray-600 mb-1">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              data-testid="payment-date-to"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No payments found</p>
          </div>
        ) : (
          <>
            <table className="w-full" data-testid="payments-table">
              <thead className="bg-gray-50 text-left text-sm text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Payment #</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Vendor / Customer</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Amount</th>
                  <th className="px-4 py-3 font-medium w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50" data-testid={`payment-row-${payment.id}`}>
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => openDetailModal(payment.id)}
                        className="font-medium text-blue-600 hover:underline"
                        data-testid={`payment-link-${payment.payment_number}`}
                      >
                        {payment.payment_number}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${
                        payment.payment_type === 'expense' 
                          ? 'bg-red-50 text-red-700' 
                          : 'bg-green-50 text-green-700'
                      }`}>
                        {payment.direction === 'outbound' 
                          ? <ArrowUpRight className="w-3 h-3" />
                          : <ArrowDownLeft className="w-3 h-3" />
                        }
                        {payment.payment_type === 'expense' ? 'Expense' : 'Sales'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {payment.vendor_name || payment.customer_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{payment.payment_date}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                      {payment.payment_method?.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        payment.status === 'void' 
                          ? 'bg-gray-100 text-gray-500' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {payment.status === 'void' ? 'Void' : 'Posted'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatCents(payment.amount_cents)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => openDetailModal(payment.id)}
                          data-testid={`view-payment-${payment.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {/* Write Check button for vendor payments */}
                        {payment.direction === 'outbound' && payment.status !== 'void' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-blue-600"
                            onClick={() => openWriteCheckModal(payment.id)}
                            title="Write Check"
                            data-testid={`write-check-${payment.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        {payment.status !== 'void' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => handleVoidPayment(payment.id)}
                            data-testid={`void-payment-${payment.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-600">
                Showing {payments.length} of {total} payments
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8" data-testid="payment-detail-modal">
            {loadingDetail ? (
              <div className="flex justify-center items-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
              </div>
            ) : paymentDetails ? (
              <>
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold">{selectedPayment.payment_number}</h3>
                    <p className="text-gray-500 text-sm">
                      {paymentDetails.vendor?.name || paymentDetails.customer?.name}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded ${
                      selectedPayment.status === 'void' 
                        ? 'bg-gray-100 text-gray-500' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedPayment.status === 'void' ? 'Void' : 'Posted'}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setShowDetailModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Payment Info */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="ml-2 capitalize">{selectedPayment.payment_type}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Direction:</span>
                      <span className="ml-2 capitalize">{selectedPayment.direction}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Date:</span>
                      <span className="ml-2">{selectedPayment.payment_date}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Method:</span>
                      <span className="ml-2 capitalize">{selectedPayment.payment_method?.replace('_', ' ')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Reference:</span>
                      <span className="ml-2">{selectedPayment.payment_reference || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Amount:</span>
                      <span className="ml-2 font-semibold">{formatCents(selectedPayment.amount_cents)}</span>
                    </div>
                  </div>
                  
                  {selectedPayment.memo && (
                    <div className="text-sm">
                      <span className="text-gray-500">Memo:</span>
                      <p className="mt-1 text-gray-700">{selectedPayment.memo}</p>
                    </div>
                  )}
                  
                  {/* Applications */}
                  <div>
                    <h4 className="font-medium mb-3">Applied To</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="payment-applications-table">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Document</th>
                            <th className="px-3 py-2 text-left font-medium">Date</th>
                            <th className="px-3 py-2 text-right font-medium">Doc Amount</th>
                            <th className="px-3 py-2 text-right font-medium">Applied</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {(paymentDetails.applications || []).length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-3 py-6 text-center text-gray-500">
                                No applications
                              </td>
                            </tr>
                          ) : (
                            paymentDetails.applications.map((app) => (
                              <tr key={app.id}>
                                <td className="px-3 py-2">
                                  <span className="text-xs text-gray-500 mr-1">{app.applies_to_type}:</span>
                                  {app.document_number || app.applies_to_id.slice(0, 8)}
                                </td>
                                <td className="px-3 py-2">{app.document_date || '—'}</td>
                                <td className="px-3 py-2 text-right">{formatCents(app.document_amount_cents || 0)}</td>
                                <td className="px-3 py-2 text-right font-medium">{formatCents(app.applied_amount_cents)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-right font-medium">Total Applied:</td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatCents(paymentDetails.applied_total_cents || 0)}
                            </td>
                          </tr>
                          <tr>
                            <td colSpan={3} className="px-3 py-2 text-right font-medium">Remaining:</td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatCents(paymentDetails.remaining_cents || 0)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                {selectedPayment.status !== 'void' && (
                  <div className="p-6 border-t bg-gray-50 flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      className="text-red-600"
                      onClick={() => handleVoidPayment(selectedPayment.id)}
                      data-testid="void-payment-detail-btn"
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Void Payment
                    </Button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Write Check from Payment Modal */}
      <Dialog open={showCheckModal} onOpenChange={setShowCheckModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Write Check from Payment</DialogTitle>
          </DialogHeader>
          
          {checkPreview && (
            <div className="space-y-4 py-4">
              {/* Payment Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payment #</span>
                  <span className="font-medium">{checkPreview.payment_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Payee</span>
                  <span className="font-medium">{checkPreview.payee_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-semibold text-lg">{formatCents(checkPreview.amount_cents)}</span>
                </div>
                <div className="text-xs text-gray-500 italic">
                  {checkPreview.amount_words}
                </div>
              </div>

              {/* Line Items */}
              {checkPreview.line_items?.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600">Bills/POs Covered</Label>
                  <div className="border rounded-lg overflow-hidden mt-1">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Bill/PO</th>
                          <th className="px-3 py-2 text-right font-medium">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {checkPreview.line_items.map((item, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">
                              {item.bill_number && <span className="text-gray-600">Bill #{item.bill_number}</span>}
                              {item.bill_number && item.po_number && <span className="mx-1">•</span>}
                              {item.po_number && <span className="text-gray-500">PO #{item.po_number}</span>}
                              {!item.bill_number && !item.po_number && item.description}
                            </td>
                            <td className="px-3 py-2 text-right">{formatCents(item.amount_cents)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Check Details Form */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="check-bank-account">Bank Account *</Label>
                  <Select value={selectedBankAccount} onValueChange={setSelectedBankAccount}>
                    <SelectTrigger id="check-bank-account" data-testid="check-bank-account-select">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.account_name} {acc.is_default && '(Default)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {bankAccounts.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No bank accounts. Add one in Settings.</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="check-date">Check Date</Label>
                  <Input
                    id="check-date"
                    type="date"
                    value={checkDate}
                    onChange={(e) => setCheckDate(e.target.value)}
                    data-testid="check-date-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="check-memo">Memo</Label>
                <Input
                  id="check-memo"
                  value={checkMemo}
                  onChange={(e) => setCheckMemo(e.target.value)}
                  placeholder="Optional memo"
                  data-testid="check-memo-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCheckModal(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleCreateCheckFromPayment(false)}
              disabled={creatingCheck || !selectedBankAccount}
              data-testid="save-draft-check-btn"
            >
              {creatingCheck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save as Draft
            </Button>
            <Button
              onClick={() => handleCreateCheckFromPayment(true)}
              disabled={creatingCheck || !selectedBankAccount}
              data-testid="create-print-check-btn"
            >
              {creatingCheck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Create & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
